import { DotsVerticalIcon } from "@heroicons/react/outline";
import {
  ISimpleEventDetail,
  ISubTicketItem,
  ITicketItem,
  Phase,
  SupportedLanguage,
} from "interfaces";
import { get, isEmpty } from "lodash";
import moment from "moment";
import { useState } from "react";
import { DropdownMenu } from "components/dropdownMenu";
import { formatNumber } from "utills/helpers";
import { TooltipCircle } from "components/tooltipCircle";
import { SubTicket } from "./subTicket";
import { NoPhaseTicket } from "./noPhaseTicket";
import { useTranslation } from "@refinedev/core";

interface SubTicketModal {
  open: boolean;
  subTicketData: ISubTicketItem | null;
}

interface SubTicketsProps {
  eventDetailData: ISimpleEventDetail;
  phaseTicket?: boolean;
  setOpenLogModalId: (id: string) => void;
  ticketData: ITicketItem;
  ticketPhases: Phase[];
}
interface NoPhaseTicketModal {
  open: boolean;
  subTicketData: ISubTicketItem | null;
}

const SUB_TICKET_MODAL_CONFIG: SubTicketModal = {
  open: false,
  subTicketData: null,
};

const NO_PHASE_TICKET_MODAL_CONFIG: NoPhaseTicketModal = {
  open: false,
  subTicketData: null,
};

export const SubTickets = ({
  eventDetailData,
  phaseTicket = true,
  ticketData,
  ticketPhases,
  setOpenLogModalId,
}: SubTicketsProps) => {
  const { translate } = useTranslation();
  const [subTicketModalConfig, setSubTicketModalConfig] = useState(
    SUB_TICKET_MODAL_CONFIG
  );
  const [noPhaseTicketModalConfig, setNoPhaseTicketModalConfig] = useState(
    NO_PHASE_TICKET_MODAL_CONFIG
  );

  const _onCloseModal = () => {
    setSubTicketModalConfig(SUB_TICKET_MODAL_CONFIG);
  };

  const _onCloseNoPhaseTicketModal = () => {
    setNoPhaseTicketModalConfig(NO_PHASE_TICKET_MODAL_CONFIG);
  };

  const subTickets = ticketData.sub_tickets ?? [];
  const firstLanguage = eventDetailData.languages.includes(SupportedLanguage.EN)
    ? "en"
    : eventDetailData.languages[0] ?? "vi";

  return (
    <>
      <div className="grid sm:grid-cols-6 lg:gap-x-6">
        <div className="col-span-full flex flex-col gap-y-4">
          {subTickets.map((subTicketField, subTicketIndex: number) => (
            <div
              className={`border rounded-lg border-gray-300`}
              key={subTicketField.id}
            >
              <div className="box-container p-0">
                <div className="w-full flex items-start gap-x-3 px-4 py-4 sm:px-6 hover:cursor-pointer">
                  <div className="flex-1 flex flex-col xl:flex-row xl:justify-between xl:items-center gap-2 sm:gap-3">
                    <p
                      className="flex-1 font-bold"
                      style={{ wordBreak: "break-word" }}
                    >
                      {!isEmpty(ticketPhases)
                        ? get(
                            ticketPhases.find(
                              (phase) =>
                                phase.id ===
                                get(subTicketField, `ticket_phase`, "")
                            )?.name,
                            firstLanguage,
                            ""
                          )
                        : get(subTicketField, `category.${firstLanguage}`, "")}
                    </p>
                    <div className="flex-1 flex flex-row items-center xl:justify-end gap-x-3 text-sm">
                      <div className="flex flex-wrap items-center xl:justify-end gap-x-3 gap-y-2">
                        <p>
                          {get(subTicketField, "sell_from", "")
                            ? moment(
                                get(subTicketField, "sell_from", "")
                              ).format("HH:mm DD/MM/YYYY")
                            : ""}{" "}
                          -{" "}
                          {get(subTicketField, "sell_to", "")
                            ? moment(get(subTicketField, "sell_to", "")).format(
                                "HH:mm DD/MM/YYYY"
                              )
                            : ""}
                        </p>
                        <p>
                          {formatNumber(get(subTicketField, "price", 0))}{" "}
                          {eventDetailData.currency}{" "}
                          <TooltipCircle
                            text={`${translate(
                              "eventSetup.phaseAndTicket.originalPrice"
                            )}: ${formatNumber(
                              get(subTicketField, "original_price", 0)
                            )} ${eventDetailData.currency} <br/>${translate(
                              "eventSetup.phaseAndTicket.sellPrice"
                            )}: ${formatNumber(
                              get(subTicketField, "price", 0)
                            )} ${eventDetailData.currency}`}
                          />
                        </p>
                        <div className="shrink-0 flex flex-row gap-x-2">
                          <span
                            className={`w-fit inline-flex items-center rounded-md px-3 py-1 text-xs font-medium ring-1 ring-inset ${
                              get(subTicketField, "is_active", false)
                                ? "bg-green-100 ring-green-100 text-green-700"
                                : "bg-magnolia-500 text-neutral-800 ring-magnolia-600"
                            }`}
                          >
                            {get(subTicketField, "is_active", false)
                              ? translate("active")
                              : translate("in-active")}
                          </span>
                          <span
                            className={`w-fit inline-flex items-center rounded-md px-3 py-1 text-xs font-medium ring-1 ring-inset ${
                              !get(subTicketField, "hide_in_ui", false)
                                ? "bg-green-100 ring-green-100 text-green-700"
                                : "bg-magnolia-500 text-neutral-800 ring-magnolia-600"
                            }`}
                          >
                            {get(subTicketField, "hide_in_ui", false)
                              ? translate("hide")
                              : translate("show")}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="shrink-0 self-center">
                    <DropdownMenu
                      label={
                        <DotsVerticalIcon className="w-4 h-4 text-brand-500" />
                      }
                      items={[
                        {
                          label: translate("view"),
                          onClick: () =>
                            phaseTicket
                              ? setSubTicketModalConfig({
                                  open: true,
                                  subTicketData: subTicketField,
                                })
                              : setNoPhaseTicketModalConfig({
                                  open: true,
                                  subTicketData: subTicketField,
                                }),
                        },
                        {
                          label: "Log",
                          onClick: () =>
                            setOpenLogModalId(subTicketField?.id ?? ""),
                        },
                      ]}
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
          <div className="text-center text-sm">
            {isEmpty(subTickets) ? <p>{translate("eventSetup.phaseAndTicket.noTickets")}</p> : <></>}
          </div>
        </div>
      </div>
      {phaseTicket ? (
        <SubTicket
          eventDetailData={eventDetailData}
          open={subTicketModalConfig.open}
          onClose={_onCloseModal}
          ticketPhases={ticketPhases}
          title={translate("eventSetup.phaseAndTicket.subTicket")}
          subTicketData={subTicketModalConfig.subTicketData}
        />
      ) : (
        <NoPhaseTicket
          subTicketData={noPhaseTicketModalConfig.subTicketData}
          eventDetailData={eventDetailData}
          open={noPhaseTicketModalConfig.open}
          onClose={_onCloseNoPhaseTicketModal}
          title={translate("eventSetup.phaseAndTicket.ticket")}
        />
      )}
    </>
  );
};
